import { ChangeDetectionStrategy, Component, type OnInit, OnDestroy, inject, signal, computed, effect } from '@angular/core';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { DeviceContextService } from '../device-context.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { ConfigEditorComponent } from '@/shared/config-editor/config-editor.component';
import { ConfigWizardComponent } from '@/cloud/components/config-wizard/config-wizard.component';

interface ConfigVersion {
  id: string;
  lastEditedUtc: string;
  createdUtc: string;
  isDefault: boolean;
}

interface ConfigFile {
  fileName: string;
  versions: ConfigVersion[];
}

interface CurrentConfig {
  path: string | null;
  fileName: string | null;
  date: string | null;
}

interface ConfigResponse {
  currentConfig: CurrentConfig;
  files: ConfigFile[];
}

interface LiveFileDetails {
  name: string;
  filepath: string;
  activeFile: boolean;
  isDefault: boolean;
  creationDate: string;
  modifiedDate: string;
}

interface TreeNode {
  fileName: string;
  isActive: boolean;
  expanded: boolean;
  versions: (ConfigVersion & { isCurrent: boolean })[];
}

@Component({
  selector: 'app-config',
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TooltipModule,
    MessageModule,
    ConfigEditorComponent,
    ConfigWizardComponent
  ],
  templateUrl: './config.html',
  styleUrl: './config.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Config implements OnInit, OnDestroy {
  private api = inject(CloudApiService);
  private ctx = inject(DeviceContextService);
  private messageService = inject(MessageService);

  // --- Page state ---
  configs = signal<ConfigResponse | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);
  currentDeviceId = computed(() => this.ctx.device()?.id ?? '');
  tunnelConnected = signal(false);

  // --- Editor state ---
  configData = signal<unknown | null>(null);
  configSchema = signal<unknown | null>(null);
  configPath = signal<string | null>(null);
  lastRevisionTime = signal<string | null>(null);
  restartRequired = signal(false);
  isDefault = signal(false);
  editorLoading = signal(false);
  editorSaving = signal(false);
  editorError = signal<string | null>(null);

  // Live mode file list
  liveFiles = signal<LiveFileDetails[]>([]);
  selectedFilePath = signal<string>('');

  fileOptions = computed(() => {
    if (this.tunnelConnected()) {
      return this.liveFiles().map(f => ({
        label: f.name + (f.isDefault ? ' (default)' : '') + (f.activeFile ? ' ●' : ''),
        value: f.filepath
      }));
    }
    const files = this.configs()?.files ?? [];
    return files.map(f => ({
      label: f.fileName,
      value: f.fileName
    }));
  });

  // --- History panel ---
  expandedNodes = signal<Set<string>>(new Set());

  historyTree = computed<TreeNode[]>(() => {
    const configResp = this.configs();
    if (!configResp?.files) return [];

    const currentFileName = configResp.currentConfig?.fileName;
    const currentDate = configResp.currentConfig?.date;
    const selectedFile = this.selectedFilePath();
    const expanded = this.expandedNodes();

    return configResp.files.map(file => {
      // Active = matches editor's current file (live filepath or offline fileName)
      const isActive = selectedFile ? (selectedFile === file.fileName || selectedFile.endsWith('/' + file.fileName) || selectedFile.endsWith('\\\\' + file.fileName)) : file.fileName === currentFileName;

      return {
        fileName: file.fileName,
        isActive,
        expanded: expanded.has(file.fileName),
        versions: file.versions.map((v, i) => ({
          ...v,
          isCurrent: isActive && i === 0
        }))
      };
    });
  });

  // --- Compare dialog ---
  compareVisible = signal(false);
  compareLoading = signal(false);
  compareHistoricalConfig = signal<any>(null);
  compareCurrentConfig = signal<any>(null);
  compareFileName = signal<string | null>(null);
  compareDate = signal<string | null>(null);

  diffLines = computed(() => {
    const historical = this.compareHistoricalConfig();
    const current = this.compareCurrentConfig();
    if (!historical || !current) return null;

    const oldLines = JSON.stringify(historical, null, 2).split('\n');
    const newLines = JSON.stringify(current, null, 2).split('\n');
    return this.computeUnifiedDiff(oldLines, newLines);
  });

  // --- Template wizard ---
  templateWizardVisible = signal(false);

  newFileSuffixText = computed(() => {
    const ns = this.configNamespace();
    return ns ? `.${ns}.config.json` : null;
  });

  downloadName = computed(() => {
    const fileName = this.liveFiles().find(f => f.activeFile)?.name || this.selectedFilePath() || 'config';
    return `${fileName}.json`;
  });

  configNamespace = computed(() => {
    // Derive namespace from existing file names (format: {name}.{namespace}.config.json)
    const files = this.liveFiles();
    for (const f of files) {
      const match = f.name.match(/\.([^.]+)\.config\.json$/);
      if (match) return match[1];
    }
    // Fallback: try from configs list
    const configFiles = this.configs()?.files ?? [];
    for (const f of configFiles) {
      const match = f.fileName.match(/\.([^.]+)\.config\.json$/);
      if (match) return match[1];
    }
    return null;
  });

  // --- Subscriptions ---
  private deviceId: string | null = null;
  private configEventSubscription?: Subscription;
  private deviceSnapshotSubscription?: Subscription;
  private liveConfigChangedSubscription?: Subscription;

  constructor() {
    effect(() => {
      const connected = this.tunnelConnected();
      const deviceId = this.currentDeviceId();
      if (!deviceId) return;
      if (!this.loading()) {
        if (connected) {
          this.loadLiveConfig();
        } else {
          this.loadOfflineConfig();
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const device = this.ctx.device();
    if (!device?.id) {
      this.error.set('No device ID available');
      this.loading.set(false);
      return;
    }

    this.deviceId = device.id;

    try {
      const data = await this.api.getDeviceConfigs(device.id);
      this.configs.set(data);

      // Auto-expand the current config file in the history tree
      if (data?.currentConfig?.fileName) {
        const expanded = new Set(this.expandedNodes());
        expanded.add(data.currentConfig.fileName);
        this.expandedNodes.set(expanded);
      }

      await this.checkTunnelStatus(device.id);

      if (this.tunnelConnected()) {
        await this.loadLiveConfig();
      } else {
        await this.loadOfflineConfig();
      }

      this.subscribeToConfigChanges(device.id);
      this.subscribeToDeviceUpdates(device.id);
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to load config files';
      this.messageService.add({ severity: 'error', summary: 'Failed to Load Configs', detail: errorMsg });
      this.error.set(errorMsg);
    } finally {
      this.loading.set(false);
    }
  }

  ngOnDestroy(): void {
    if (this.deviceId) {
      this.api.unsubscribe(`devices:${this.deviceId}:configs:created`);
      this.api.unsubscribe(`devices:${this.deviceId}:configs:updated`);
      this.api.unsubscribe(`device:${this.deviceId}`);
      this.api.unsubscribe(`device:${this.deviceId}:config:changed`);
    }
    this.configEventSubscription?.unsubscribe();
    this.deviceSnapshotSubscription?.unsubscribe();
    this.liveConfigChangedSubscription?.unsubscribe();
  }

  // =============================================
  // EDITOR: Live mode
  // =============================================

  async loadLiveConfig(): Promise<void> {
    if (!this.deviceId) return;
    this.editorLoading.set(true);
    this.editorError.set(null);

    try {
      const data = await this.api.getLiveConfig(this.deviceId);
      this.applyLiveConfigData(data);
    } catch (e: any) {
      const msg = e?.error?.error || e?.message || 'Failed to load live config';
      this.editorError.set(msg);
    } finally {
      this.editorLoading.set(false);
    }
  }

  private applyLiveConfigData(data: any): void {
    this.configData.set(data.config);
    this.configSchema.set(data.schema ?? null);
    this.configPath.set(data.configPath);
    this.lastRevisionTime.set(data.lastRevisionTime);
    this.restartRequired.set(data.restartRequired ?? false);
    this.isDefault.set(data.isDefault ?? false);
    this.liveFiles.set(data.availableFiles ?? []);

    const activeFile = (data.availableFiles as LiveFileDetails[])?.find(f => f.activeFile);
    this.selectedFilePath.set(activeFile?.filepath || '');

    // Auto-expand the active file's tree node
    if (activeFile?.name) {
      const expanded = new Set(this.expandedNodes());
      expanded.add(activeFile.name);
      this.expandedNodes.set(expanded);
    }
  }

  async switchFile(filePath: string): Promise<void> {
    if (!filePath) return;

    if (this.tunnelConnected()) {
      if (filePath === this.selectedFilePath()) return;
      this.editorLoading.set(true);
      try {
        const data = await this.api.loadLiveConfig(this.deviceId!, filePath);
        this.applyLiveConfigData(data);
      } catch (e: any) {
        const msg = e?.error?.error || e?.message || 'Failed to switch config';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
      } finally {
        this.editorLoading.set(false);
      }
    } else {
      if (filePath === this.selectedFilePath()) return;
      this.selectedFilePath.set(filePath);
      await this.loadOfflineConfigForFile(filePath);
    }
  }

  async save(json: unknown): Promise<void> {
    if (!this.deviceId) return;
    this.editorSaving.set(true);
    try {
      const data = await this.api.setLiveConfig(this.deviceId, json);
      this.applyLiveConfigData(data);
      this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Config saved to device' });
      this.reloadConfigs();
      setTimeout(() => this.reloadConfigs(), 3000);
    } catch (e: any) {
      const msg = e?.error?.error || e?.message || 'Failed to save config';
      this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
    } finally {
      this.editorSaving.set(false);
    }
  }

  reload(): void {
    if (this.tunnelConnected()) {
      this.loadLiveConfig();
    } else {
      this.loadOfflineConfig();
    }
  }

  async createNewConfig(name: string): Promise<void> {
    if (!name || !this.deviceId) return;
    this.editorSaving.set(true);
    try {
      const data = await this.api.createLiveConfig(this.deviceId, name);
      this.applyLiveConfigData(data);
      this.messageService.add({ severity: 'success', summary: 'Created', detail: `Config file "${name}" created` });
      this.reloadConfigs();
      setTimeout(() => this.reloadConfigs(), 3000);
    } catch (e: any) {
      const msg = e?.error?.error || e?.message || 'Failed to create config';
      this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
    } finally {
      this.editorSaving.set(false);
    }
  }

  async deleteConfig(): Promise<void> {
    if (!this.deviceId) return;
    this.editorSaving.set(true);
    try {
      const data = await this.api.deleteLiveConfig(this.deviceId);
      this.applyLiveConfigData(data);
      this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Config file deleted' });
      this.reloadConfigs();
      setTimeout(() => this.reloadConfigs(), 3000);
    } catch (e: any) {
      const msg = e?.error?.error || e?.message || 'Failed to delete config';
      this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
    } finally {
      this.editorSaving.set(false);
    }
  }

  // =============================================
  // EDITOR: Offline mode
  // =============================================

  async loadOfflineConfig(): Promise<void> {
    if (!this.deviceId) return;
    const configResp = this.configs();
    if (!configResp?.files?.length) return;

    const currentFileName = configResp.currentConfig?.fileName;
    const targetFile = configResp.files.find(f => f.fileName === currentFileName) ?? configResp.files[0];

    this.selectedFilePath.set(targetFile.fileName);
    await this.loadOfflineConfigForFile(targetFile.fileName);
  }

  private async loadOfflineConfigForFile(fileName: string): Promise<void> {
    if (!this.deviceId) return;
    const configResp = this.configs();
    const file = configResp?.files?.find(f => f.fileName === fileName);
    if (!file?.versions?.length) {
      this.configData.set(null);
      return;
    }

    const latestVersion = file.versions[0];
    this.editorLoading.set(true);

    try {
      const config = await this.api.getDeviceConfig(this.deviceId, latestVersion.id);
      if (config?.payload) {
        this.configData.set(config.payload);
        this.configSchema.set(config.schema ?? null);
        this.configPath.set(fileName);
        this.lastRevisionTime.set(latestVersion.lastEditedUtc);
        this.isDefault.set(latestVersion.isDefault);
        this.restartRequired.set(false);
      }
    } catch (e: any) {
      const msg = e?.error?.error || e?.message || 'Failed to load config';
      this.editorError.set(msg);
    } finally {
      this.editorLoading.set(false);
    }
  }

  // =============================================
  // HISTORY PANEL
  // =============================================

  toggleTreeNode(node: TreeNode): void {
    const expanded = new Set(this.expandedNodes());
    if (expanded.has(node.fileName)) {
      expanded.delete(node.fileName);
    } else {
      expanded.add(node.fileName);
    }
    this.expandedNodes.set(expanded);
  }

  async compareVersion(fileName: string, version: ConfigVersion): Promise<void> {
    if (!this.deviceId) return;

    this.compareFileName.set(fileName);
    this.compareDate.set(version.lastEditedUtc);
    this.compareVisible.set(true);
    this.compareLoading.set(true);

    this.compareCurrentConfig.set(this.configData());

    try {
      const config = await this.api.getDeviceConfig(this.deviceId, version.id);
      if (config?.payload) {
        this.compareHistoricalConfig.set(config.payload);
      }
    } catch (e: any) {
      const msg = e?.error?.error || e?.message || 'Failed to load historical config';
      this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
      this.compareVisible.set(false);
    } finally {
      this.compareLoading.set(false);
    }
  }

  closeCompare(): void {
    this.compareVisible.set(false);
    this.compareHistoricalConfig.set(null);
    this.compareCurrentConfig.set(null);
    this.compareFileName.set(null);
    this.compareDate.set(null);
  }

  private computeUnifiedDiff(oldLines: string[], newLines: string[]): { lines: { type: string; content: string; oldNum: number | null; newNum: number | null }[]; additions: number; deletions: number } {
    // LCS-based diff
    const m = oldLines.length;
    const n = newLines.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = oldLines[i - 1] === newLines[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }

    // Backtrack to build diff
    const result: { type: string; content: string; oldNum: number | null; newNum: number | null }[] = [];
    let i = m, j = n;
    const stack: { type: string; content: string; oldIdx: number; newIdx: number }[] = [];

    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
        stack.push({ type: 'context', content: oldLines[i - 1], oldIdx: i, newIdx: j });
        i--; j--;
      } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
        stack.push({ type: 'added', content: newLines[j - 1], oldIdx: 0, newIdx: j });
        j--;
      } else {
        stack.push({ type: 'removed', content: oldLines[i - 1], oldIdx: i, newIdx: 0 });
        i--;
      }
    }

    stack.reverse();

    let additions = 0;
    let deletions = 0;
    for (const entry of stack) {
      if (entry.type === 'added') additions++;
      if (entry.type === 'removed') deletions++;
      result.push({
        type: entry.type,
        content: entry.content,
        oldNum: entry.type !== 'added' ? entry.oldIdx : null,
        newNum: entry.type !== 'removed' ? entry.newIdx : null,
      });
    }

    return { lines: result, additions, deletions };
  }

  async downloadVersion(fileName: string, version: ConfigVersion): Promise<void> {
    if (!this.deviceId) return;
    try {
      const config = await this.api.getDeviceConfig(this.deviceId, version.id);
      if (!config?.payload) throw new Error('Config not found');

      const jsonStr = JSON.stringify(config.payload, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date(version.lastEditedUtc);
      const dateStr = date.toISOString().slice(0, 10);
      const timeStr = date.toISOString().slice(11, 19).replace(/:/g, '-');
      link.download = `${fileName}_${dateStr}_${timeStr}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      this.messageService.add({ severity: 'error', summary: 'Download Failed', detail: e?.message || 'Error' });
    }
  }

  refreshHistory(): void {
    this.reloadConfigs();
  }

  // =============================================
  // TEMPLATE WIZARD
  // =============================================

  showTemplateWizard(): void {
    this.templateWizardVisible.set(true);
  }

  onTemplateApplied(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Template Applied',
      detail: 'Device is restarting with the new configuration.'
    });
    setTimeout(() => {
      this.reloadConfigs();
      if (this.tunnelConnected()) this.loadLiveConfig();
    }, 5000);
  }

  // =============================================
  // INTERNALS
  // =============================================

  private isCurrentVersion(fileName: string, versionDate: string, currentFileName: string | null | undefined, currentDate: string | null | undefined): boolean {
    if (!currentFileName || !currentDate) return false;
    if (fileName !== currentFileName) return false;
    const diff = Math.abs(new Date(currentDate).getTime() - new Date(versionDate).getTime());
    return diff < 2000;
  }

  private async checkTunnelStatus(deviceId: string): Promise<void> {
    try {
      const status = await this.api.getDeviceTunnelStatus(deviceId);
      this.tunnelConnected.set(status.tunnelConnected);
    } catch {
      this.tunnelConnected.set(false);
    }
  }

  private subscribeToConfigChanges(deviceId: string): void {
    this.api.subscribe(`devices:${deviceId}:configs:created`);
    this.api.subscribe(`devices:${deviceId}:configs:updated`);
    this.configEventSubscription = this.api.getDeviceConfigEvents(deviceId).subscribe(event => {
      if (event) this.reloadConfigs();
    });
  }

  private subscribeToDeviceUpdates(deviceId: string): void {
    this.api.subscribe(`device:${deviceId}`);
    this.api.subscribe(`device:${deviceId}:config:changed`);

    this.liveConfigChangedSubscription = this.api.getDeviceLiveConfigChanged(deviceId).subscribe(() => {
      if (this.tunnelConnected()) {
        this.loadLiveConfig();
      }
    });

    this.deviceSnapshotSubscription = this.api.getDeviceStream(deviceId).subscribe(device => {
      if (device && (device as any).tunnelConnected !== undefined) {
        this.tunnelConnected.set(!!(device as any).tunnelConnected);
      }
    });
  }

  private async reloadConfigs(): Promise<void> {
    if (!this.deviceId) return;
    try {
      const data = await this.api.getDeviceConfigs(this.deviceId);
      this.configs.set(data);
    } catch { /* silent */ }
  }
}
